import {
  acknowledgePortalInspection,
  getPortalDocumentUrl,
  useListPortalInspections,
  type PortalInspectionOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate, formatDateTime } from '../../lib/format';
import { byArea, CONDITION, KIND } from '../maintenance/inspectionLabels';
import { NotLinked } from './NotLinked';
import { usePortalHome } from './usePortalHome';

function InspectionCard({ inspection }: { inspection: PortalInspectionOut }) {
  const queryClient = useQueryClient();
  const [comments, setComments] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function acknowledge() {
    setBusy(true);
    setError(null);
    try {
      await acknowledgePortalInspection(inspection.id, { comments: comments.trim() || null });
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function view(id: number) {
    setError(null);
    try {
      const link = okData(await getPortalDocumentUrl(id));
      if (link) window.location.assign(link.url);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {KIND[inspection.kind]} Inspection: {inspection.place}
          </Typography>
          <Button size="small" href={`/api/portal/inspections/${inspection.id}/pdf`}>
            Download PDF
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {formatDate(inspection.inspected_on)}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {byArea(inspection.items).map(([area, items]) => (
          <div key={area}>
            <Typography sx={{ fontWeight: 600 }}>{area}</Typography>
            {items.map((item) => (
              <Stack
                key={`${item.area}-${item.name}`}
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', py: 0.5, flexWrap: 'wrap' }}
              >
                <Typography sx={{ flexGrow: 1 }}>{item.name}</Typography>
                {item.notes && (
                  <Typography variant="body2" color="text.secondary">
                    {item.notes}
                  </Typography>
                )}
                {item.condition && (
                  <Chip
                    size="small"
                    color={CONDITION[item.condition].color}
                    label={CONDITION[item.condition].label}
                  />
                )}
              </Stack>
            ))}
          </div>
        ))}
        {inspection.notes && <Typography>{inspection.notes}</Typography>}
        {inspection.photos.length > 0 && (
          <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
            {inspection.photos.map((photo) => (
              <Button key={photo.id} size="small" onClick={() => void view(photo.id)}>
                {photo.file_name}
              </Button>
            ))}
          </Stack>
        )}
        {inspection.resident_ack_at ? (
          <Alert severity="success">
            You acknowledged this on {formatDateTime(inspection.resident_ack_at)}.
            {inspection.resident_comments && ` You wrote: "${inspection.resident_comments}"`}
          </Alert>
        ) : (
          <Stack spacing={1}>
            <TextField
              multiline
              minRows={2}
              label="Anything You Disagree With?"
              helperText="Optional. Your comments stay with this inspection, and you can't change them later."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 1000 } }}
            />
            <Button
              variant="contained"
              disabled={busy}
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => void acknowledge()}
            >
              I&apos;ve read this
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

export function PortalInspectionsPage() {
  const { home, notLinked } = usePortalHome();
  const inspections = okItems(useListPortalInspections({ query: { enabled: !!home } }).data);
  if (notLinked) return <NotLinked />;
  if (!home) return null;
  return (
    <Stack spacing={3}>
      <Typography variant="h4">Inspections</Typography>
      {inspections.length === 0 ? (
        <Typography color="text.secondary">
          Inspections of your home will show here once they&apos;re completed.
        </Typography>
      ) : (
        inspections.map((inspection) => (
          <InspectionCard key={inspection.id} inspection={inspection} />
        ))
      )}
    </Stack>
  );
}
